'use strict';

import React, {Component} from 'react';
import PropTypes from "prop-types";
import {observer} from "mobx-react";
import Loader from "@uw-it-sis/lib-react/lib/Loader";
import DocumentTitle from '@uw-it-sis/lib-react/lib/DocumentTitle';
import {LinkContainer} from "react-router-bootstrap";
import {Link} from "react-router-dom";

import {FaCaretLeft, FaCaretRight, FaBan} from "react-icons/fa";

import Spinner from "react-bootstrap/Spinner";
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Button from "react-bootstrap/Button";

import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import SimpleState from "@uw-it-sis/lib-react/lib/SimpleState";
import RelatedLinks from "@uw-it-sis/lib-react-myplan/lib/RelatedLinks";
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import {States} from "@uw-it-sis/lib-react/lib/AppConstants";

import planViewStore from "./PlanViewStore";
import PlanTerm from "./PlanTerm";
import PlanItemModal from "../registration/PlanItemModal";
import Modal from "react-bootstrap/Modal";

class PlanView extends Component {

    constructor(props) {
        super(props);
        this.props = props;
        this.store = planViewStore;
    }

    componentDidMount() {
        let year = this.props.academicYear.substring(0, 4);
        this.store.mount(year);
    }

    componentDidUpdate(prevProps) {
        if ( this.props.academicYear !== prevProps.academicYear ) {
            let year = this.props.academicYear.substring(0, 4);
            this.store.mount(year);
        }
    }

    componentWillUnmount() {
        this.store.unmount();
    }

    hideFailureModal = () => {
        this.store.clearFailureMessage();
    }

    buildDoneComponent = () => {
        let planTerms = this.store.getPlanData().map((term, i) => {
            let termShortName = TermUtils.makeTermName(term.atpId, "SHORT");
            return (
                <Col md={6} lg={3} key={i} className="mt-md-5 mt-lg-0">
                    <PlanTerm term={term} isAnchored={this.props.scrollTo === termShortName} termShortName={termShortName} />
                </Col>
            );
        });

        let failureMessage = null;
        if ( this.store.showFailureMessageModal ) {
            failureMessage =
                <Modal id="modal-failure-message"
                       show={this.store.showFailureMessageModal}
                       onHide={this.hideFailureModal}
                       aria-labelledby="modal-failure-message-title"
                       centered>
                    <Modal.Header closeButton>
                        <Modal.Title id="modal-failure-message-title">{this.store.failureTitle}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="d-flex align-items-start text-danger">
                            <FaBan className="me-2 mt-1" aria-hidden={true} />
                            <div>
                                {this.store.failureMessage}
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={this.hideFailureModal}>Close</Button>
                    </Modal.Footer>
                </Modal>;
        }

        const loaderSpinner =
            <Spinner
                animation="dots"
                role="status">
                <span className="sr-only">Loading Your Plan...</span>
            </Spinner>;

        return (
            <>
                <Loader
                    show={this.store.state === States.pending}
                    message={loaderSpinner}
                    contentBlur={1}
                    disableDefaultStyles>
                    <Row className="g-2 mb-4 align-content-stretch space-y-5 space-y-lg-0">
                        {planTerms}
                        {failureMessage}
                    </Row>
                </Loader>
                <PlanItemModal/>
            </>
        );
    }

    render() {
        let relatedLinks = null;
        if (this.props.relatedLinks) {
            relatedLinks = <RelatedLinks links={this.props.relatedLinks} />;
        }

        // Create a disabled nav button if navigating to previous year isnt allowed
        let prevYearNav = <Button variant="navigate" aria-label={`No previous academic year available`} className="px-0 py-1" disabled><FaCaretLeft aria-hidden="true" size="1.75rem"  /></Button>;
        // Create a disabled nav button if navigating to next year isnt allowed
        let nextYearNav = <Button variant="navigate" aria-label={`No next academic year available`} className="px-0 py-1" disabled><FaCaretRight aria-hidden="true" size="1.75rem"  /></Button>;

        // Use 'dots' animation for first time load of course section data
        let pendingComponent = "dots";
        // If data is already present, keeping showing sections list while add/remove sections action is taking place.
        if ( this.store.hasData() ) {
            pendingComponent = this.buildDoneComponent;

            // If a previous academic year is provided, set prev button to navigate to that year
            if ( this.props.prev ) {
                let prevTitle = `Previous academic year: ${this.props.prev}`;
                prevYearNav =
                       <LinkContainer to={`/${this.props.prev}`}>
                           <Button variant="navigate" title={prevTitle} aria-label={prevTitle} className="px-0 py-1"><FaCaretLeft aria-hidden="true" size="1.75rem"  /></Button>
                       </LinkContainer>;
            }

            // If a future academic year is provided, set next button to navigate to that year
            if ( this.props.next ) {
                let nextTitle = `Next academic year: ${this.props.next}`
                nextYearNav =
                       <LinkContainer to={`/${this.props.next}`}>
                           <Button variant="navigate" title={nextTitle} aria-label={nextTitle} className="px-0 py-1"><FaCaretRight aria-hidden="true" size="1.75rem"  /></Button>
                       </LinkContainer>;
            }
        }

        return (
            <DocumentTitle title={`${this.props.academicYear} Plan - MyPlan`}>
                <>
                    <div className="page-heading d-flex align-items-start">
                        <h1 className="flex-grow-1">Academic Year {this.props.academicYear}</h1>
                        <ButtonGroup aria-label="Academic Year Navigation" className="ms-3">
                            {prevYearNav}
                            {nextYearNav}
                        </ButtonGroup>
                    </div>
                    <div className="text-end mb-3 space-x-3">
                        <Link to={'/print'}>View entire plan (read-only)</Link>
                    </div>
                    <SimpleState state={this.store.state} doneComponentBuilder={this.buildDoneComponent} pendingComponentBuilder={pendingComponent} name="PlanView"/>
                    {relatedLinks}
                </>
            </DocumentTitle>
        );
    }
}

PlanView.propTypes = {
    academicYear: PropTypes.string.isRequired,
    scrollTo: PropTypes.string, // a term short name would be given if jumping to term is required
    prev: PropTypes.string, // an academic year code which can be navigated to
    next: PropTypes.string, // an academic year code which can be navigated to
    relatedLinks: PropTypes.array
};

export default observer(PlanView);